import {
  MAX_PERSISTED_DETAILED_ANSWER_CHARACTERS,
  parseErrorCode,
  parseSuggestions,
  safeStreamMessage,
  type ParsedSseEvent,
  type ResearchSuggestion,
  type ReviewAnnotation,
} from './risResearchStream';
import type { RunEventApplier } from './researchRunStore';

/**
 * Der Abruf der ausführlichen Schicht, auf Anforderung des Lesers. Seit die
 * Release-Linie den Klartext allein schickt, steht die ausführliche Antwort
 * erst bereit, wenn der Leser sie aufklappt; ein Lauf der Art detail holt sie
 * (ResearchRunStore.startDetail).
 *
 * Der Zustandsübergang steht als reine Funktion für sich: die Zuordnung von
 * Ereignis und Anzeige lässt sich damit ohne Browser prüfen.
 */

/**
 * Die Zeichengrenze der Speicherung ist zugleich die des Abrufs. Eine
 * ausführliche Schicht darüber nähme der Server nicht an: sie stünde einmal
 * da und wäre beim Wiederladen fort. Der Grund steht deshalb sofort da.
 */
const MAX_DETAIL_CHARACTERS = MAX_PERSISTED_DETAILED_ANSWER_CHARACTERS;

/** Der Hinweis, solange die ausführliche Antwort noch entsteht. */
export const DETAIL_PENDING_MESSAGE = 'Ausführliche Antwort wird erstellt …';

/**
 * Derselbe Hinweis mit den verstrichenen Sekunden. Erstellt ein gleichzeitiger
 * Lauf dieselbe Schicht, wartet dieser Abruf auf ihn und die Gegenseite meldet
 * das alle fünf Sekunden; ohne die Zahl stünde die Oberfläche scheinbar still.
 */
export function detailWaitingMessage(waitedS: number): string {
  return `${DETAIL_PENDING_MESSAGE} (${Math.max(0, Math.round(waitedS))} s)`;
}

/** Die feste Meldung einer zu großen Schicht. */
const DETAIL_TOO_LARGE_MESSAGE = 'Die ausführliche Antwort ist zu umfangreich und wurde abgebrochen.';

export type DetailStreamStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface DetailStreamState {
  status: DetailStreamStatus;
  /** Der bisher empfangene Text; beim Abschluss der maßgebliche Volltext. */
  text: string;
  /** Der Hinweis, der während des Wartens unter der Antwort steht. */
  statusMessage: string;
  suggestions?: ResearchSuggestion[];
  reviewAnnotation?: ReviewAnnotation;
  errorCode?: string;
  errorMessage?: string;
  /** Der Lauf startete nach einem Serverneustart neu (run.attempt_reset): ein Hinweis bis zu seinem Ende. */
  restartNotice?: string;
}

export const INITIAL_DETAIL_STATE: DetailStreamState = {
  status: 'idle',
  text: '',
  statusMessage: '',
};

export type DetailStreamEvent =
  | { type: 'start' }
  | { type: 'status'; message: string }
  | { type: 'detail_waiting'; waitedS: number; maxWaitS?: number }
  | { type: 'detail_delta'; text: string }
  | { type: 'detail_restart' }
  | {
    type: 'detail_done';
    detailedAnswer: string;
    suggestions?: ResearchSuggestion[];
    reviewAnnotation?: ReviewAnnotation;
  }
  | { type: 'error'; code: string; message: string };

/** Mehr als einen Tag wartet niemand; alles darüber ist eine entgleiste Zahl. */
const MAX_WAIT_SECONDS = 86_400;

function waitSeconds(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > MAX_WAIT_SECONDS) {
    return undefined;
  }
  return Math.round(value);
}

function parseReviewAnnotation(value: unknown): ReviewAnnotation | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const status = record.status;
  if (status !== 'certified' && status !== 'objections' && status !== 'unavailable') return undefined;
  const objections = Array.isArray(record.objections)
    ? record.objections.flatMap((item) => {
      if (typeof item !== 'object' || item === null || Array.isArray(item)) return [];
      const entry = item as Record<string, unknown>;
      const claimText = typeof entry.claimText === 'string' ? entry.claimText : '';
      const reasoning = typeof entry.reasoning === 'string' ? entry.reasoning : '';
      const kind = typeof entry.kind === 'string' ? entry.kind : undefined;
      const origin = typeof entry.origin === 'string' ? entry.origin : undefined;
      if (!claimText || !reasoning || (!kind && !origin)) return [];
      return [{ claimText, reasoning, ...(kind ? { kind } : {}), ...(origin ? { origin } : {}) }];
    })
    : [];
  const reviewedAt = typeof record.reviewedAt === 'string' && record.reviewedAt ? record.reviewedAt : undefined;
  return { status, objections, ...(reviewedAt ? { reviewedAt } : {}) };
}

/**
 * Ein Ereignis der Leitung in eines dieser Schicht. Ein unbekannter Name --
 * etwa ein Lebenszeichen eines neueren Servers -- ergibt nichts und lässt den
 * Zustand unverändert.
 */
export function parseDetailStreamEvent(event: ParsedSseEvent | null): DetailStreamEvent | null {
  if (!event) return null;
  const payload = event.payload;
  if (event.type === 'detail_delta') {
    const text = typeof payload.text === 'string' ? payload.text : '';
    return text ? { type: 'detail_delta', text } : null;
  }
  if (event.type === 'detail_restart') return { type: 'detail_restart' };
  if (event.type === 'detail_done') {
    const detailedAnswer = typeof payload.detailedAnswer === 'string' ? payload.detailedAnswer : '';
    if (!detailedAnswer.trim()) return null;
    const metadata = typeof payload.metadata === 'object' && payload.metadata !== null && !Array.isArray(payload.metadata)
      ? payload.metadata as Record<string, unknown>
      : {};
    const suggestions = parseSuggestions(payload.suggestions);
    const reviewAnnotation = parseReviewAnnotation(metadata.reviewAnnotation);
    return {
      type: 'detail_done',
      detailedAnswer,
      ...(suggestions ? { suggestions } : {}),
      ...(reviewAnnotation ? { reviewAnnotation } : {}),
    };
  }
  if (event.type === 'detail_waiting') {
    const waitedS = waitSeconds(payload.waitedS);
    if (waitedS === undefined) return null;
    const maxWaitS = waitSeconds(payload.maxWaitS);
    return { type: 'detail_waiting', waitedS, ...(maxWaitS !== undefined ? { maxWaitS } : {}) };
  }
  if (event.type === 'status') {
    return { type: 'status', message: safeStreamMessage(payload.message, DETAIL_PENDING_MESSAGE) };
  }
  if (event.type === 'error') {
    const code = parseErrorCode(payload.code) || 'DETAIL_FAILED';
    return {
      type: 'error',
      code,
      message: safeStreamMessage(
        payload.error ?? payload.message,
        'Die ausführliche Antwort konnte nicht abgerufen werden.',
      ),
    };
  }
  return null;
}

/**
 * Der Zustand der aufgeklappten Schicht nach einem Ereignis. Ein Neustart
 * verwirft das bisher Angezeigte, der Abschluss ersetzt es durch den
 * maßgeblichen Text, ein Fehler lässt den bisherigen Text stehen -- der Leser
 * behält dann, was er schon gelesen hat, und sieht daneben den Grund.
 */
export function detailStreamReducer(
  state: DetailStreamState,
  event: DetailStreamEvent,
): DetailStreamState {
  switch (event.type) {
    case 'start':
      return { status: 'loading', text: '', statusMessage: DETAIL_PENDING_MESSAGE };
    case 'status':
      return state.status === 'ready' ? state : { ...state, status: 'loading', statusMessage: event.message };
    case 'detail_waiting':
      return state.status === 'ready'
        ? state
        : { ...state, status: 'loading', statusMessage: detailWaitingMessage(event.waitedS) };
    case 'detail_delta': {
      const text = state.text + event.text;
      if (text.length > MAX_DETAIL_CHARACTERS) {
        return {
          ...state,
          status: 'error',
          statusMessage: '',
          errorCode: 'DETAIL_RESPONSE_TOO_LARGE',
          errorMessage: DETAIL_TOO_LARGE_MESSAGE,
        };
      }
      return { ...state, status: 'loading', text, statusMessage: DETAIL_PENDING_MESSAGE };
    }
    case 'detail_restart':
      return { status: 'loading', text: '', statusMessage: 'Ausführliche Antwort wird neu erstellt …' };
    case 'detail_done':
      return {
        status: 'ready',
        text: event.detailedAnswer,
        statusMessage: '',
        ...(event.suggestions ? { suggestions: event.suggestions } : {}),
        ...(event.reviewAnnotation ? { reviewAnnotation: event.reviewAnnotation } : {}),
      };
    case 'error':
      return {
        ...state,
        status: 'error',
        statusMessage: '',
        errorCode: event.code,
        errorMessage: event.message,
      };
    default:
      return state;
  }
}

/**
 * Der Auswerter des Laufs der ausführlichen Schicht (Ticket 09) für den Laufspeicher: derselbe Zustandsübergang je
 * Ereignis wie am bisherigen Abruf. Ein neuer Versuch bekommt einen neuen Auswerter und verwirft damit den Teiltext.
 */
export function createDetailEventApplier(
  onUpdate: (state: DetailStreamState) => void = () => undefined,
): RunEventApplier<DetailStreamState> {
  let state = detailStreamReducer(INITIAL_DETAIL_STATE, { type: 'start' });
  return {
    apply(event) {
      const parsed = parseDetailStreamEvent(event);
      if (!parsed) return;
      state = detailStreamReducer(state, parsed);
      onUpdate(state);
    },
    snapshot: () => state,
    failure: () => (state.status === 'error' && state.errorMessage
      ? { code: state.errorCode || 'DETAIL_FAILED', message: state.errorMessage }
      : null),
    finish: () => state,
  };
}

/** Die Sätze eines Laufs der ausführlichen Schicht, der ohne sie endete; sonst gilt der Satz des Servers. */
const DETAIL_RUN_END_MESSAGES: Readonly<Record<string, string>> = {
  TIMEOUT: 'Die ausführliche Antwort hat das Zeitlimit erreicht.',
  RUN_CANCELLED: 'Die ausführliche Antwort wurde abgebrochen.',
  RUN_INTERRUPTED: 'Die ausführliche Antwort wurde unterbrochen. Bitte erneut versuchen.',
};

/** Der Zustand nach dem Ende eines Laufs ohne ausführliche Schicht (fehlgeschlagen, abgebrochen, unterbrochen). */
export function detailRunEndState(state: DetailStreamState, code: string, message: string): DetailStreamState {
  return detailStreamReducer(state, { type: 'error', code, message: DETAIL_RUN_END_MESSAGES[code] || message });
}
